import React from 'react';
import {
  Alert,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { CARE_LANGUAGE_LABELS } from '../../constants/config';
import {
  deleteDecisionReceipt,
  fetchDecisionReceiptsForCandidateKeys,
  RECEIPT_ACTIONS,
} from '../reviewableItems/reviewableDecisionReceipts';
import { getReviewableItemAdapter } from '../reviewableItems/reviewableItemAdapters';
import { markCareKnowledgeSourceReviewComplete } from './careSourceActivityService';
import {
  buildReviewableCandidatesFromCareSource,
  getStructuredCandidateDestination,
} from './careSourceReviewableCandidates';
import ScrollableReviewSection from './ScrollableReviewSection';
import CollapsibleReviewGroup from './CollapsibleReviewGroup';
import CompactReviewRow from './CompactReviewRow';
import GoalCandidateReviewSheet from './GoalCandidateReviewSheet';
import {
  buildMedicationDraftFromCandidate,
  getMedicationReviewCandidateMatches,
} from '../../utils/medicationReviewCandidate';
import HealthCandidateReviewSheet from './HealthCandidateReviewSheet';

const HANDLED_RECEIPT_ACTIONS = new Set([
  RECEIPT_ACTIONS.RESOLVED,
  RECEIPT_ACTIONS.IGNORED,
  RECEIPT_ACTIONS.AUTO_APPLIED,
]);

const getDestinationCopy = (domain = '') => {
  if (domain === 'allergy') {
    return {
      singular: 'Allergy',
      plural: 'Allergies',
      saved: 'Allergy added',
      prompt: 'Add to Allergies?',
      action: 'Add allergy',
      destination: 'Allergies',
    };
  }

  if (domain === 'timeline_fact') {
    return {
      singular: 'Timeline item',
      plural: 'Timeline',
      saved: 'Timeline updated',
      prompt: 'Add to Timeline?',
      action: 'Add to timeline',
      destination: 'Timeline',
    };
  }

  if (domain === 'medication') {
    return {
      singular: 'Medication',
      plural: 'Medications',
      saved: 'Medication added',
      prompt: 'Add to Medications?',
      action: 'Add medication',
      destination: 'Medications',
    };
  }

  if (domain === 'document') {
    return {
      singular: 'Document section',
      plural: 'Document',
      saved: 'Document reviewed',
      prompt: 'Keep in Document?',
      action: 'Review document',
      destination: 'Document',
    };
  }

  return {
    singular: 'Goal',
    plural: 'Goals',
    saved: CARE_LANGUAGE_LABELS.goalSaved,
    prompt: 'Save as goal?',
    action: 'Add goal',
    destination: 'Goals',
  };
};

export const buildReviewDestinationCounts = (candidates = []) => (
  candidates.reduce((counts, candidate) => {
    const label = getDestinationCopy(candidate.domain).destination;
    return { ...counts, [label]: (counts[label] || 0) + 1 };
  }, {})
);

const getDomainForDestination = (destination = '') => {
  if (destination === 'Allergies') return 'allergy';
  if (destination === 'Medications') return 'medication';
  if (destination === 'Timeline') return 'timeline_fact';
  if (destination === 'Document') return 'document';
  return 'goal';
};

const formatDestinationCounts = (counts = {}) => (
  Object.entries(counts)
    .map(([label, count]) => `${count} ${count === 1 ? getDestinationCopy(getDomainForDestination(label)).singular : label}`)
    .join(' · ')
);

const CareSourceReviewableCandidateActions = ({
  source = {},
  candidates: providedCandidates,
  hasGenericSuggestions = false,
  onError,
  onVisibleCandidatesChange,
  timelinePresentation = false,
  existingMedications = [],
}) => {
  const [handledKeys, setHandledKeys] = React.useState(new Set());
  const [addedKeys, setAddedKeys] = React.useState(new Set());
  const [skippedKeys, setSkippedKeys] = React.useState(new Set());
  const [loadingReceipts, setLoadingReceipts] = React.useState(false);
  const [savingKey, setSavingKey] = React.useState('');
  const [goalCandidateToReview, setGoalCandidateToReview] = React.useState(null);
  const [healthCandidateToReview, setHealthCandidateToReview] = React.useState(null);
  const usesProvidedCandidates = Array.isArray(providedCandidates);
  const reviewSourceIdentity = source.id || source.childId || 'care-source-review';
  const candidates = React.useMemo(() => (
    usesProvidedCandidates
      ? providedCandidates
      : buildReviewableCandidatesFromCareSource(source)
  ), [providedCandidates, source, usesProvidedCandidates]);
  const hasOnlyGoalCandidates = candidates.length > 0
    && candidates.every((candidate) => candidate.domain === 'goal');
  const hasOnlyHealthCandidates = candidates.length > 0
    && candidates.every((candidate) => ['allergy', 'medication'].includes(candidate.domain));

  React.useEffect(() => {
    if (!usesProvidedCandidates) return;
    setHandledKeys(new Set());
    setAddedKeys(new Set());
    setSkippedKeys(new Set());
    setLoadingReceipts(false);
  }, [reviewSourceIdentity, usesProvidedCandidates]);

  React.useEffect(() => {
    let active = true;
    if (usesProvidedCandidates) {
      return undefined;
    }
    const keys = candidates
      .filter((candidate) => candidate.domain !== 'document')
      .map((candidate) => candidate.key)
      .filter(Boolean);

    if (!keys.length) {
      setHandledKeys(new Set());
      setSkippedKeys(new Set());
      return () => {
        active = false;
      };
    }

    setLoadingReceipts(true);
    Promise.resolve(fetchDecisionReceiptsForCandidateKeys(keys))
      .then((receipts) => {
        if (!active) return;
        setHandledKeys(new Set((receipts || [])
          .filter((receipt) => HANDLED_RECEIPT_ACTIONS.has(receipt.action))
          .map((receipt) => receipt.candidateKey || receipt.id)
          .filter(Boolean)));
        setAddedKeys(new Set((receipts || [])
          .filter((receipt) => receipt.action === RECEIPT_ACTIONS.RESOLVED
            || receipt.action === RECEIPT_ACTIONS.AUTO_APPLIED)
          .map((receipt) => receipt.candidateKey || receipt.id)
          .filter(Boolean)));
        // A skipped candidate remains hidden after the sheet is reopened. skippedKeys is
        // reserved for the current session so Undo can remain calm and immediate.
        setSkippedKeys(new Set());
      })
      .catch((error) => {
        if (active) {
          setHandledKeys(new Set());
          setAddedKeys(new Set());
          setSkippedKeys(new Set());
          if (process.env.NODE_ENV !== 'production') {
            // Receipt reads are only used to hide already-handled candidates. If rules reject
            // the lookup, keep review actions available instead of blocking the caregiver.
            // eslint-disable-next-line no-console
            console.warn(error?.message || "Couldn't check existing review receipts.");
          }
        }
      })
      .finally(() => {
        if (active) {
          setLoadingReceipts(false);
        }
      });

    return () => {
      active = false;
    };
  }, [candidates, usesProvidedCandidates]);

  const reviewableCandidates = candidates.filter((candidate) => candidate.domain !== 'document');
  const documentCandidates = candidates.filter((candidate) => candidate.domain === 'document');
  const visibleReviewableCandidates = reviewableCandidates
    .filter((candidate) => !handledKeys.has(candidate.key));
  const visibleCandidates = [...visibleReviewableCandidates, ...documentCandidates];
  const candidateGroups = visibleCandidates.reduce((groups, candidate) => {
    const destination = getStructuredCandidateDestination(candidate.domain);
    if (!groups.has(destination)) groups.set(destination, []);
    groups.get(destination).push(candidate);
    return groups;
  }, new Map());
  const currentGoalCandidateCount = reviewableCandidates
    .filter((candidate) => candidate.domain === 'goal').length;
  const goalReviewSessionRef = React.useRef({
    sourceIdentity: reviewSourceIdentity,
    total: currentGoalCandidateCount,
  });
  if (goalReviewSessionRef.current.sourceIdentity !== reviewSourceIdentity) {
    goalReviewSessionRef.current = {
      sourceIdentity: reviewSourceIdentity,
      total: currentGoalCandidateCount,
    };
  } else if (currentGoalCandidateCount > goalReviewSessionRef.current.total) {
    goalReviewSessionRef.current.total = currentGoalCandidateCount;
  }
  const goalCandidateCount = goalReviewSessionRef.current.total;
  const destinationSummary = formatDestinationCounts(buildReviewDestinationCounts(visibleCandidates));

  React.useEffect(() => {
    if (loadingReceipts) return;
    onVisibleCandidatesChange?.({
      count: visibleReviewableCandidates.filter((candidate) => !skippedKeys.has(candidate.key)).length,
      destinations: buildReviewDestinationCounts(visibleReviewableCandidates),
    });
  }, [loadingReceipts, onVisibleCandidatesChange, skippedKeys, visibleReviewableCandidates]);

  if (!visibleCandidates.length) {
    return null;
  }

  const handleDecision = async (candidate, decisionAction, extraContext = {}) => {
    setSavingKey(`${candidate.key}:${decisionAction}`);
    onError?.('');

    try {
      const adapter = getReviewableItemAdapter(candidate.domain);
      await adapter.completeDecision({
        candidate,
        decisionAction,
        context: {
          childId: source.childId || candidate.origin?.childId || '',
          audit: {
            sourceId: source.id || '',
            sourceContext: source.sourceContext || source.sourceMetadata?.sourceContext || '',
          },
          ...(candidate.domain === 'medication' && decisionAction !== 'ignore'
            ? { medication: buildMedicationDraftFromCandidate(candidate) }
            : {}),
          ...extraContext,
        },
      });

      const nextHandledKeys = decisionAction === 'ignore'
        ? new Set(handledKeys)
        : new Set([...handledKeys, candidate.key]);
      setHandledKeys(nextHandledKeys);
      const nextSkippedKeys = decisionAction === 'ignore'
        ? new Set([...skippedKeys, candidate.key])
        : new Set(skippedKeys);
      if (decisionAction === 'ignore') {
        setSkippedKeys(nextSkippedKeys);
      }
      if (decisionAction !== 'ignore') {
        setAddedKeys((current) => new Set([...current, candidate.key]));
      }

      const decidedKeyCount = new Set([...nextHandledKeys, ...nextSkippedKeys]).size;
      const decisionTargetCount = hasOnlyGoalCandidates
        ? goalCandidateCount
        : reviewableCandidates.length;
      if (!hasGenericSuggestions && decidedKeyCount >= decisionTargetCount) {
        await markCareKnowledgeSourceReviewComplete(source.id);
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:care-knowledge-refresh', {
          detail: {
            childId: source.childId || null,
            sourceId: source.id || null,
            domain: candidate.domain,
          },
        }));
      }
      return true;
    } catch (error) {
      onError?.(error?.message || "We couldn't finish that review action.");
      return false;
    } finally {
      setSavingKey('');
    }
  };

  const handleUndoSkip = async (candidate) => {
    setSavingKey(`${candidate.key}:undo`);
    onError?.('');
    try {
      await deleteDecisionReceipt(candidate.key);
      setHandledKeys((current) => {
        const next = new Set(current);
        next.delete(candidate.key);
        return next;
      });
      setSkippedKeys((current) => {
        const next = new Set(current);
        next.delete(candidate.key);
        return next;
      });
    } catch (error) {
      onError?.(error?.message || "We couldn't undo that choice.");
    } finally {
      setSavingKey('');
    }
  };

  const goalAnchorId = hasOnlyGoalCandidates && source.id
    ? `timeline-goals-to-track-${source.id}`
    : undefined;
  const openHealthReview = (candidate) => {
    const matchedMedication = candidate.domain === 'medication'
      ? getMedicationReviewCandidateMatches(candidate, existingMedications).exactMatch
      : null;
    setHealthCandidateToReview({ ...candidate, matchedMedication });
  };

  return (
    <Box
      id={goalAnchorId}
      data-timeline-goals-to-track={hasOnlyGoalCandidates ? 'true' : undefined}
      sx={{ mx: { xs: -0.2, sm: 0 }, py: { xs: 1, sm: 1.15 }, scrollMarginTop: 24 }}
    >
      <Stack spacing={1}>
        {visibleReviewableCandidates.length ? <ScrollableReviewSection
          title={timelinePresentation ? 'Needs Review' : 'Needs your review'}
          listAriaLabel="Items that need your review"
          header={(
            <Box>
              <Typography sx={{ fontSize: { xs: '1.18rem', sm: '1.35rem' }, fontWeight: hasOnlyGoalCandidates ? 800 : 950, color: colors.semantic.ink, lineHeight: 1.15 }}>
                {hasOnlyGoalCandidates
                  ? `${goalCandidateCount} goal${goalCandidateCount === 1 ? '' : 's'} need${goalCandidateCount === 1 ? 's' : ''} your review`
                  : destinationSummary}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: '0.82rem', color: alpha(colors.brand.deep, 0.68), lineHeight: 1.35 }}>
                {hasOnlyHealthCandidates
                  ? 'Confirm, correct, or reject each health detail before it is added to the Care Profile.'
                  : 'Quick add a complete goal, or review and edit it first.'}
              </Typography>
            </Box>
          )}
        >
          <Stack spacing={1}>
            {loadingReceipts ? (
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Checking review history...
              </Alert>
            ) : null}

            {[...candidateGroups.entries()].map(([destination, groupCandidates]) => (
              destination === 'Document' ? null :
              <CollapsibleReviewGroup
                key={destination}
                label={destination}
                count={destination === 'Goals' ? goalCandidateCount : groupCandidates.length}
                destinationId={`review-destination-${destination.toLowerCase()}`}
                defaultOpen={destination !== 'Goals'}
                titleWeight={destination === 'Goals' ? 700 : 850}
                showReviewedCount={destination !== 'Document'}
                progressLabel={`${addedKeys.size} of ${goalCandidateCount} added`}
              >
                {groupCandidates.map((candidate) => {
                  const medicationMatch = candidate.domain === 'medication'
                    ? getMedicationReviewCandidateMatches(candidate, existingMedications).exactMatch
                    : null;
                  const addAction = candidate.domain === 'medication'
                    ? medicationMatch ? 'update_existing' : 'add_new'
                    : candidate.allowedActions.find((action) => action !== 'ignore');
                  const isTemporaryHold = candidate.domain === 'medication'
                    && candidate.suggestedAction === 'temporary_hold';
                  const isDiscontinuation = candidate.domain === 'medication'
                    && candidate.suggestedAction === 'discontinue';
                  const isHistoricalSnapshot = candidate.domain === 'medication'
                    && candidate.suggestedAction === 'historical_snapshot';
                  const isCourseCompletion = candidate.domain === 'medication'
                    && candidate.suggestedAction === 'complete_course';
                  const isClarification = candidate.domain === 'medication'
                    && candidate.suggestedAction === 'needs_clarification';
                  return (
                    <CompactReviewRow
                      key={candidate.key}
                      text={candidate.label}
                      destination={destination}
                      reason={candidate.extracted?.reason || candidate.payload?.reason}
                      originalText={candidate.sourceText || candidate.extracted?.sourceText}
                      context={candidate.payload?.description || candidate.payload?.value}
                      decision={skippedKeys.has(candidate.key) ? 'skip' : undefined}
                      readOnly={candidate.domain === 'document'}
                      disabled={Boolean(savingKey)}
                      quickActionLabel={isHistoricalSnapshot ? 'Review history' : isTemporaryHold ? 'Review dates' : isDiscontinuation ? 'Review stop' : isCourseCompletion ? 'Review completion' : isClarification ? 'Clarify' : undefined}
                      onQuickAdd={() => (isTemporaryHold || isDiscontinuation || isHistoricalSnapshot || isCourseCompletion || isClarification
                        ? openHealthReview(candidate)
                        : handleDecision(candidate, addAction, candidate.domain === 'medication' ? {
                            medication: {
                              ...(medicationMatch || {}),
                              ...buildMedicationDraftFromCandidate(candidate),
                              ...(medicationMatch?.id ? { id: medicationMatch.id } : {}),
                            },
                            previousMedication: medicationMatch || null,
                          } : {}))}
                      onReview={isTemporaryHold || isDiscontinuation || isHistoricalSnapshot || isCourseCompletion
                        ? undefined
                        : candidate.domain === 'goal'
                        ? () => setGoalCandidateToReview(candidate)
                        : ['allergy', 'medication'].includes(candidate.domain)
                          ? () => openHealthReview(candidate)
                          : undefined}
                      onSkip={() => handleDecision(candidate, 'ignore')}
                      onUndo={() => handleUndoSkip(candidate)}
                    />
                  );
                })}
              </CollapsibleReviewGroup>
            ))}
          </Stack>
        </ScrollableReviewSection> : null}
        {documentCandidates.length ? (
          <CollapsibleReviewGroup label="Document" count={documentCandidates.length} showReviewedCount={false} defaultOpen={false}>
            {documentCandidates.map((candidate) => (
              <CompactReviewRow key={candidate.key} text={candidate.label} readOnly reason={candidate.extracted?.reason || candidate.payload?.reason} originalText={candidate.sourceText || candidate.extracted?.sourceText} context={candidate.payload?.description || candidate.payload?.value} />
            ))}
          </CollapsibleReviewGroup>
        ) : null}
      </Stack>
      <GoalCandidateReviewSheet
        open={Boolean(goalCandidateToReview)}
        candidate={goalCandidateToReview}
        saving={Boolean(savingKey)}
        onClose={() => setGoalCandidateToReview(null)}
        onSave={async (goal) => {
          const candidate = goalCandidateToReview;
          const saved = await handleDecision(candidate, 'add_goal', { goal });
          if (saved) {
            setGoalCandidateToReview(null);
          }
        }}
      />
      {healthCandidateToReview ? (
        <HealthCandidateReviewSheet
          open
          candidate={healthCandidateToReview}
          saving={Boolean(savingKey)}
          onClose={() => setHealthCandidateToReview(null)}
          onSave={async (reviewedDetail) => {
            const candidate = healthCandidateToReview;
            const isTemporaryHold = candidate.suggestedAction === 'temporary_hold';
            const isDiscontinuation = candidate.suggestedAction === 'discontinue';
            const isHistoricalSnapshot = candidate.suggestedAction === 'historical_snapshot';
            const isCourseCompletion = candidate.suggestedAction === 'complete_course';
            const isClarification = candidate.suggestedAction === 'needs_clarification';
            const decisionAction = candidate.domain === 'medication'
              ? isHistoricalSnapshot
                ? reviewedDetail?.decisionAction || 'historical_snapshot'
              : isDiscontinuation
                ? 'discontinue'
                : isCourseCompletion
                ? 'complete_course'
                : isTemporaryHold
                ? 'temporary_hold'
                : candidate.matchedMedication ? 'update_existing' : 'add_new'
              : candidate.allowedActions.find((action) => action !== 'ignore');
            const extraContext = candidate.domain === 'medication'
              ? {
                  ...((isTemporaryHold || isClarification) ? { createTimelineEvent: true } : {}),
                  medication: {
                    ...(candidate.matchedMedication || {}),
                    ...buildMedicationDraftFromCandidate(candidate),
                    ...(typeof reviewedDetail === 'object' ? reviewedDetail : { name: reviewedDetail }),
                    ...(candidate.matchedMedication?.id ? { id: candidate.matchedMedication.id } : {}),
                  },
                  previousMedication: candidate.matchedMedication || null,
                }
              : { allergy: reviewedDetail };
            const saved = await handleDecision(candidate, decisionAction, extraContext);
            if (saved) setHealthCandidateToReview(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default CareSourceReviewableCandidateActions;
